import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  COMMON_BORDER_STYLES,
  PILL_COLORS,
  STATUS_PILL_PALETTES,
  STATUS_PILL_VARIANT_MAP
} from './pill-standard-colours';

@Component({
  selector: 'hf-custom-pill',
  templateUrl: './custom-pill.component.html',
  styleUrls: ['./custom-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomPillComponent implements OnInit, OnChanges {
  private static readonly PILL_CONFIG = {
    WHATSAPP: { color: PILL_COLORS.whatsapp, icon: 'assets/icons/svg/whatsapp.svg' },
    EMAIL: { color: PILL_COLORS.email, icon: 'assets/icons/svg/email.svg' },
    TRANSCRIPT: { color: PILL_COLORS.phone, icon: 'assets/icons/svg/phone.svg' },
    DOCUMENT: { color: PILL_COLORS.document, icon: 'assets/icons/svg/document.svg' },
    FACT_FIND: { color: PILL_COLORS.factfind, icon: 'assets/icons/svg/factfind.svg' }
  } as const;

  @Input() label: string;
  @Input() sublabel?: string;
  @Input() icon?: string;
  @Input() altText?: string;
  @Input() canClick?: boolean = false;
  @Input() textWeight: 'regular' | 'medium' = 'regular';

  // Custom icon
  @Input() iconType: 'material' | 'image' | 'none' = 'material';
  @Input() iconName?: string; // for material icon
  @Input() imageSrc?: string; // for image icon
  public resolvedIconName: string;
  public resolvedImageSrc: string;
  @Input() imageWidth?: string;
  @Input() imageHeight?: string;

  @Input() type: 'WHATSAPP' | 'EMAIL' | 'TRANSCRIPT' | 'DOCUMENT' | 'FACT_FIND' | 'CUSTOM' = 'CUSTOM';

  // Custom colours
  @Input() primaryColour?: string;
  @Input() backgroundColour?: string;

  @Input() customStyles: { [key: string]: string } = {};

  @Input() status?: keyof typeof STATUS_PILL_VARIANT_MAP;

  public styleIconPill: { [key: string]: string } = {};

  constructor() {}

  ngOnInit(): void {
    this.initializePill();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.status || changes.type || changes.primaryColour || changes.backgroundColour || changes.customStyles) {
      this.initializePill();
    }
  }

  private updateStyles(): void {
    if (this.type !== 'CUSTOM') {
      this.styleIconPill = {
        'border-color': this.adjustColor(this.primaryColour, 50),
        color: this.adjustColor(this.primaryColour, -25),
        'background-color': this.adjustColor(this.primaryColour, 90),
        ...this.customStyles
      };
    } else {
      const variant = this.status && (STATUS_PILL_VARIANT_MAP[this.status.toLowerCase()] || 'gray');
      const palette = variant && STATUS_PILL_PALETTES[variant];
      this.styleIconPill = {
        color: palette?.color || this.primaryColour,
        'background-color': palette?.background || this.backgroundColour,
        ...(palette && { border: `1px solid ${palette.border}` }),
        ...this.customStyles
      };
    }
  }

  private initializePill(): void {
    this.resolvePillStylesAndIcon();
    this.updateStyles();
  }

  private resolvePillStylesAndIcon() {
    if (this.type !== 'CUSTOM') {
      this.customStyles = { ...COMMON_BORDER_STYLES };
      this.iconType = 'image';

      const config = CustomPillComponent.PILL_CONFIG[this.type];
      if (config) {
        this.primaryColour ||= config.color;
        this.resolvedImageSrc = config.icon;
      }
    }

    // Allow overrides
    this.resolvedIconName = this.iconName || this.resolvedIconName;
    this.resolvedImageSrc = this.imageSrc || this.resolvedImageSrc;
  }

  private adjustColor(hex: string, percent: number): string {
    if (!hex) return '#000000';

    // Strip leading '#'
    let raw = hex.replace(/^#/, '');

    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
      raw = raw
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }

    // Validate after potential expansion (#RRGGBB or #RRGGBBAA)
    if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(raw)) {
      console.warn(`Invalid hex color: ${hex}`);
      return '#000000'; // fallback
    }

    const hasAlpha = raw.length === 8;
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    const a = hasAlpha ? raw.slice(6, 8) : null;

    const p = Math.max(-100, Math.min(100, percent)) / 100;

    // For p >= 0, move toward 255; for p < 0, scale toward 0
    const adj = (x: number) =>
      p >= 0 ? Math.min(255, Math.round(x + (255 - x) * p)) : Math.max(0, Math.round(x * (1 + p)));

    const R = adj(r);
    const G = adj(g);
    const B = adj(b);

    const toHex = (n: number) => n.toString(16).padStart(2, '0');

    return '#' + toHex(R) + toHex(G) + toHex(B) + (a ?? '');
  }
}
